import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { useForm } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'
import { AlertTriangle, CreditCard, RotateCcw, ArrowRight } from 'lucide-react'

import type { StripePayment } from '@/gen/cortado/v1/invoices_pb'
import { ProcessStripeRefundRequestSchema } from '@/gen/cortado/v1/invoices_pb'
import { useProcessStripeRefund, getRefundableAmount, formatStripePaymentMethod } from '@/stores/payments.store'
import { formatCurrency, dollarsToCents } from '@/lib/currency-utils'
import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Slider } from '@/components/ui/slider'

interface RefundDialogProps {
  invoiceId: string
  payment: StripePayment | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

type RefundType = 'full' | 'partial'

export function RefundDialog({
  invoiceId,
  payment,
  open,
  onOpenChange,
  onSuccess,
}: RefundDialogProps) {
  const { processRefund, isProcessing } = useProcessStripeRefund(invoiceId)

  const refundableAmountCents = payment ? Number(getRefundableAmount(payment)) : 0
  const refundableAmountDollars = refundableAmountCents / 100
  const originalAmountCents = payment ? Number(payment.amountCents) : 0
  const alreadyRefundedCents = payment ? Number(payment.amountRefundedCents) : 0

  const form = useForm({
    defaultValues: {
      refundType: 'full' as RefundType,
      amount: refundableAmountDollars,
      reason: '',
      willRepay: false, // false = goodwill refund (default), true = customer will repay
    },
    onSubmit: async ({ value }) => {
      try {
        if (!payment) {
          toast.error('No payment selected')
          return
        }

        let amountCents: number
        if (value.refundType === 'full') {
          amountCents = refundableAmountCents
        } else {
          amountCents = dollarsToCents(value.amount)
          if (amountCents <= 0) {
            toast.error('Refund amount must be greater than 0')
            return
          }
          if (amountCents > refundableAmountCents) {
            toast.error(`Refund amount exceeds maximum of ${formatCurrency(refundableAmountCents)}`)
            return
          }
        }

        const request = create(ProcessStripeRefundRequestSchema, {
          paymentId: payment.id,
          amountCents: BigInt(amountCents),
          reason: value.reason || undefined,
          isGoodwill: !value.willRepay,
        })

        await processRefund(request)

        toast.success('Refund processed successfully')
        onOpenChange(false)
        form.reset()
        onSuccess?.()
      } catch (error) {
        console.error('Failed to process refund:', error)
        reportError(error, { feature: 'payment_refund', kind: 'mutation' })
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'refund-dialog' })
        toast.error('Failed to process refund. Please try again.', {
          description: getErrorDescription(error),
        })
      }
    },
  })

  // Reset form when dialog opens with new payment
  React.useEffect(() => {
    if (open && payment) {
      const newRefundableAmount = Number(getRefundableAmount(payment)) / 100
      form.setFieldValue('amount', newRefundableAmount)
      form.setFieldValue('refundType', 'full')
      form.setFieldValue('reason', '')
      form.setFieldValue('willRepay', false)
    }
  }, [open, payment])

  // Calculate preview amounts (in dollars for display)
  const currentRefundAmountDollars = form.state.values.refundType === 'full'
    ? refundableAmountDollars
    : form.state.values.amount
  const currentRefundAmountCents = dollarsToCents(currentRefundAmountDollars)
  const alreadyRefundedDollars = alreadyRefundedCents / 100
  const originalAmountDollars = originalAmountCents / 100
  const newRefundedTotal = alreadyRefundedDollars + currentRefundAmountDollars
  const remainingAfterRefund = originalAmountDollars - newRefundedTotal

  if (!payment) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-purple-500" />
            Process Refund
          </DialogTitle>
          <DialogDescription>
            Issue a refund through Stripe for this payment.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-6"
        >
          {/* Payment Preview Card */}
          <div className="rounded-xl border-2 border-purple-100 dark:border-purple-900/50 bg-gradient-to-br from-purple-50/80 to-card dark:from-purple-950/30 dark:to-background p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
                <CreditCard className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{formatStripePaymentMethod(payment)}</p>
                <p className="font-financial text-xs text-muted-foreground">
                  {payment.stripeChargeId}
                </p>
              </div>
            </div>

            {/* Amount breakdown */}
            <div className="space-y-2 pt-2 border-t border-purple-100 dark:border-purple-800">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Original amount</span>
                <span className="font-financial font-medium">{formatCurrency(originalAmountCents)}</span>
              </div>
              {alreadyRefundedCents > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Already refunded</span>
                  <span className="font-financial text-purple-600 dark:text-purple-400">
                    -{formatCurrency(alreadyRefundedCents)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm font-medium">
                <span>Available for refund</span>
                <span className="font-financial text-purple-600 dark:text-purple-400">
                  {formatCurrency(refundableAmountCents)}
                </span>
              </div>
            </div>
          </div>

          {/* Refund Type Selection */}
          <form.Field name="refundType">
            {(field) => (
              <div className="space-y-3">
                <Label>
                  Refund Amount <span className="text-destructive">*</span>
                </Label>
                <RadioGroup
                  value={field.state.value}
                  onValueChange={(v: string) => {
                    field.handleChange(v as RefundType)
                    if (v === 'full') {
                      form.setFieldValue('amount', refundableAmountDollars)
                    }
                  }}
                  className="grid grid-cols-2 gap-3"
                >
                  <label
                    htmlFor="full"
                    className={cn(
                      'flex items-center justify-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-all',
                      field.state.value === 'full'
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    )}
                  >
                    <RadioGroupItem value="full" id="full" className="sr-only" />
                    <div className="text-center">
                      <p className="font-financial font-semibold">{formatCurrency(refundableAmountCents)}</p>
                      <p className="text-xs text-muted-foreground">Full refund</p>
                    </div>
                  </label>
                  <label
                    htmlFor="partial"
                    className={cn(
                      'flex items-center justify-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-all',
                      field.state.value === 'partial'
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    )}
                  >
                    <RadioGroupItem value="partial" id="partial" className="sr-only" />
                    <div className="text-center">
                      <p className="font-semibold">Custom</p>
                      <p className="text-xs text-muted-foreground">Partial refund</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            )}
          </form.Field>

          {/* Partial Amount Controls */}
          <form.Subscribe selector={(state) => state.values.refundType}>
            {(refundType) =>
              refundType === 'partial' && (
                <form.Field name="amount">
                  {(field) => (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label htmlFor="amount">
                            Refund Amount <span className="text-destructive">*</span>
                          </Label>
                          <span className="font-financial text-sm text-muted-foreground">
                            Max: {formatCurrency(refundableAmountCents)}
                          </span>
                        </div>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                            $
                          </span>
                          <Input
                            id="amount"
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={refundableAmountDollars}
                            className="font-financial pl-7 text-lg font-semibold h-12"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                      {/* Amount slider */}
                      <Slider
                        value={[field.state.value]}
                        onValueChange={([value]) => field.handleChange(value)}
                        max={refundableAmountDollars}
                        min={0.01}
                        step={0.01}
                        className="w-full"
                      />
                      {/* Quick amounts */}
                      <div className="flex gap-2">
                        {[25, 50, 75].map((percent) => (
                          <Button
                            key={percent}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => field.handleChange(Math.round(refundableAmountDollars * percent) / 100)}
                          >
                            {percent}%
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </form.Field>
              )
            }
          </form.Subscribe>

          {/* Refund Preview */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">After refund</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 text-center p-2 rounded bg-background">
                <p className="font-financial text-lg font-bold text-purple-600 dark:text-purple-400">
                  -{formatCurrency(currentRefundAmountCents)}
                </p>
                <p className="text-xs text-muted-foreground">Refunding</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 text-center p-2 rounded bg-background">
                <p className="font-financial text-lg font-bold">
                  {formatCurrency(dollarsToCents(remainingAfterRefund))}
                </p>
                <p className="text-xs text-muted-foreground">Net received</p>
              </div>
            </div>
          </div>

          {/* Repayment Question */}
          <form.Field name="willRepay">
            {(field) => (
              <div className="space-y-3">
                <Label>Will customer repay this amount?</Label>
                <RadioGroup
                  value={field.state.value ? 'yes' : 'no'}
                  onValueChange={(v: string) => field.handleChange(v === 'yes')}
                  className="grid grid-cols-2 gap-3"
                >
                  <label
                    htmlFor="willRepay-no"
                    className={cn(
                      'flex items-center justify-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-all',
                      !field.state.value
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    )}
                  >
                    <RadioGroupItem value="no" id="willRepay-no" className="sr-only" />
                    <div className="text-center">
                      <p className="font-semibold">No</p>
                      <p className="text-xs text-muted-foreground">Goodwill refund</p>
                    </div>
                  </label>
                  <label
                    htmlFor="willRepay-yes"
                    className={cn(
                      'flex items-center justify-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-all',
                      field.state.value
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    )}
                  >
                    <RadioGroupItem value="yes" id="willRepay-yes" className="sr-only" />
                    <div className="text-center">
                      <p className="font-semibold">Yes</p>
                      <p className="text-xs text-muted-foreground">Customer will repay</p>
                    </div>
                  </label>
                </RadioGroup>
                <p className="text-xs text-muted-foreground">
                  {field.state.value
                    ? 'Invoice will show as partially paid until customer pays again.'
                    : 'Invoice total will be reduced. It may show as fully paid.'}
                </p>
              </div>
            )}
          </form.Field>

          {/* Reason */}
          <form.Field name="reason">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="reason">
                  Reason <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Textarea
                  id="reason"
                  placeholder="Reason for refund (internal note)"
                  rows={2}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          {/* Warning */}
          <Alert variant="destructive" className="border-destructive/50 bg-destructive/5">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>This action cannot be undone</AlertTitle>
            <AlertDescription>
              Refunds are processed immediately through Stripe.
            </AlertDescription>
          </Alert>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <form.Subscribe selector={(s) => [s.values.refundType, s.values.amount, s.isSubmitting] as const}>
              {([refundType, amount, isSubmitting]) => {
                const partialInvalid = refundType === 'partial' && (amount <= 0 || dollarsToCents(amount) > refundableAmountCents)
                return (
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={isProcessing || isSubmitting || refundableAmountCents <= 0 || partialInvalid}
                  >
                    {isProcessing ? 'Processing...' : `Refund ${formatCurrency(currentRefundAmountCents)}`}
                  </Button>
                )
              }}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
